import React, { useState, useEffect, useRef, forwardRef, useMemo, useCallback } from "react";
import ReactQuill, { Quill } from "react-quill";
import ImageResize from "quill-image-resize-module-react";
import toastr from "toastr";
import "react-quill/dist/quill.snow.css";
import useHttp from 'app/shared/hooks/useHttp';

import "quill-emoji/dist/quill-emoji.css";
import "quill-emoji/dist/quill-emoji.js";
import BlotFormatter from 'quill-blot-formatter';

import TableHandler, { rewirteFormats } from 'quill1.3.7-table-module';
import 'quill1.3.7-table-module/dist/index.css';

Quill.register("modules/imageResize", ImageResize);
Quill.register("modules/blotFormatter", BlotFormatter);

Quill.register({ [`modules/${TableHandler.moduleName}`]: TableHandler }, true);
rewirteFormats();

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    .ql-editor table {
      border-collapse: collapse;
      width: 100%;
    }
    .ql-editor table td {
      border: 1px solid #ccc;
      padding: 8px;
    }
    
    .ql-toolbar {
      background: #f8f9fa;
      border: none !important;
      flex-wrap: wrap;
      padding: 8px !important;
    }
    
    .ql-container {
      border: none !important;
      background: white;
      font-size: 14px;
      position: relative;
      z-index: 1;
    }
    
    .ql-editor {
      min-height: 250px !important;
      max-height: 500px;
      overflow-y: auto;
      padding: 15px !important;
    }
    
    /* Estilo para o botão da tabela ficar visível */
    .ql-toolbar button {
      width: 28px;
      height: 28px;
      padding: 3px 5px;
    }
    
    .ql-toolbar .ql-formats {
      margin-right: 12px;
      position: relative;
      z-index: 2;
    }

    .ql-editor table {
      border-collapse: collapse;
      width: 100%;
      margin: 1rem 0;
      font-size: 14px;
    }

    /* Estiliza as células */
    .ql-editor td, 
    .ql-editor th {
      border: 1px solid #e2e8f0;
      padding: 8px 12px;
      vertical-align: top;
    }

    /* Efeito hover nas linhas (opcional) */
    .ql-editor tr:hover {
      background-color: #f9fafb;
    }

    /* Cabeçalho da tabela se você usar <th> */
    .ql-editor th {
      background-color: #f7f9fc;
      font-weight: 600;
      border-bottom: 2px solid #e2e8f0;
    }

    /* Ajuste para o redimensionamento de colunas */
    .quill-table-drag-resize {
      background-color: #0589f3;
      opacity: 0.6;
    }

    /* MENU DA TABELA */
    .quill-table-operation-menu {
      z-index: 9999 !important;
      position: absolute !important;
    }

    /* seleção da tabela */
    .quill-table-selection {
      z-index: 999 !important;
    }

    .ql-custom-select {
      display: none !important;
    }

    /* Só aparece quando passar o mouse no botão */
    .ql-table.ql-expanded:hover .ql-custom-select {
      display: block !important;
      position: absolute;
      z-index: 9999;
    }

    .ql-table:after {
      display: none !important;
    }

    /* Remove o texto em chinês */
    .ql-table-picker span {
      display: none !important;
    }

    /* Ajusta o container pra não sobrar espaço */
    .ql-table-picker {
      padding: 5px !important;
    }

    .ql-table-wrapper Table {
      margin: 0 auto !important;
    }

    .create_select_custom {
      display: none !important;
    }

    .create_select_custom::after {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
}

const QuillEditor = forwardRef((props, ref) => (
  <ReactQuill ref={ref} {...props} />
));

export default function RichTextComponent({ onChange, defaultValue = "", }) {
  const [content, setContent] = useState(defaultValue || "");
  const quillRef = useRef(null);
  const api = useHttp();

  useEffect(() => {
    const handler = (event) => {
      console.warn("Promise ignorada (bug tabela):", event.reason);
      event.preventDefault();
    };

    window.addEventListener("unhandledrejection", handler);

    return () => {
      window.removeEventListener("unhandledrejection", handler);
    };
  }, []);

  useEffect(() => {
    const translations = {
      '在左侧插入一列': 'Coluna à esquerda',
      '在右侧插入一列': 'Coluna à direita',
      '在上方插入一行': 'Linha acima',
      '在下方插入一行': 'Linha abaixo',
      '删除所在列': 'Remover coluna',
      '删除所在行': 'Remover linha',
      '删除表格': 'Remover tabela',
      '合并单元格': 'Mesclar células',
      '拆分单元格': 'Dividir células',
      '设置背景颜色': 'Definir cor de fundo',
      '清除背景颜色': 'Limpar cor de fundo',
      '设置边框颜色': 'Definir cor da borda',
      '清除边框颜色': 'Limpar cor da borda',
      '自定义行列数': 'Personalizar linhas e colunas',
    };

    const translateMenu = (node) => {
      const items = node.querySelectorAll
        ? node.querySelectorAll('.quill-table-operation-menu-item, [class*="operation-menu"] span, [class*="table-menu"] li')
        : [];

      items.forEach(item => {
        const text = item.innerText?.trim();
        if (text && translations[text]) {
          item.innerText = translations[text];
        }
      });

      // Também tenta traduzir qualquer nó de texto
      const walk = (el) => {
        if (!el) return;
        el.childNodes.forEach(child => {
          if (child.nodeType === Node.TEXT_NODE) {
            const t = child.textContent.trim();
            if (translations[t]) {
              child.textContent = translations[t];
            }
          } else {
            walk(child);
          }
        });
      };
      walk(node);
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Verifica se é o menu da tabela
            if (
              node.className?.includes?.('table') ||
              node.className?.includes?.('operation') ||
              node.querySelector?.('[class*="table"]')
            ) {
              translateMenu(node);
              // Pequeno delay para garantir que o DOM finalizou
              setTimeout(() => translateMenu(node), 50);
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    handleChange(content, true);
  }, []);

  const handleChange = (html, firstCheck = false) => {
    const plainText = html.replace(/<[^>]+>/g, " ").trim();
    const words = plainText ? plainText.split(/\s+/).length : 0;
    const chars = plainText.length;

    onChange?.(
      {
        html,
        plainText,
        counters: { words, chars },
      },
      firstCheck
    );

    setContent(html);
  };

  const insertHorizontalRule = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection(true);
      quill.insertText(range.index, '\n');
      quill.insertEmbed(range.index + 1, 'horizontalRule', true);
      quill.setSelection(range.index + 2, 0);
    }
  }, []);

  const imageHandler = useMemo(
    () => () => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.click();

      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
          toastr.error("A imagem deve ter no máximo 5MB");
          return;
        }

        const formData = new FormData();
        formData.append("file", file);

        try {
          const result = await api.post('/upload', formData);

          const fullUrl = result?.data?.url;

          if (!fullUrl) {
            throw new Error("URL da imagem não retornada");
          }

          // Extrair apenas o caminho após '/shared/'
          const sharedPathMatch = fullUrl.match(/\/shared\/(.+)/);

          let imageUrl;

          if (sharedPathMatch) {
            imageUrl = `/shared/${sharedPathMatch[1]}`;
          } else {
            imageUrl = fullUrl;
          }

          const quill = quillRef.current?.getEditor();
          if (!quill) return;

          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, "image", imageUrl);
          quill.setSelection(range.index + 1);

        } catch (err) {
          console.error("Erro no upload:", err);
          toastr.error("Erro ao fazer upload da imagem");
        }
      };
    },
    []
  );

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

        ['bold', 'italic', 'underline', 'strike'],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        [{ 'color': [] }, { 'background': [] }],

        [{ 'align': [] }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],

        [{ 'list': 'ordered' }, { 'list': 'bullet' }],

        ['blockquote', 'code-block'],

        ['link', 'image'],

        [TableHandler.toolName],

        ['clean']
      ],
      handlers: {
        image: imageHandler,
        emoji: function () {
        }
      }
    },
    imageResize: {
      modules: ['Resize', 'DisplaySize', 'Toolbar'],
      handleStyles: {
        backgroundColor: 'black',
        border: 'none',
        color: 'white'
      },
    },
    blotFormatter: {
    },
    'emoji-toolbar': true,
    'emoji-textarea': false,
    'emoji-shortname': true,
    [`${TableHandler.moduleName}`]: {
      fullWidth: true,
      dragResize: true,
      selection: {
        primaryColor: '#0589f3',
      },
      operationMenu: {
        items: {
          insertColumnLeft: { text: 'Coluna à esquerda' },
          insertColumnRight: { text: 'Coluna à direita' },
          insertRowTop: { text: 'Linha acima' },
          insertRowBottom: { text: 'Linha abaixo' },
          removeRow: { text: 'Remover linha' },
          removeCol: { text: 'Remover coluna' },
          removeTable: { text: 'Remover tabela' },
          mergeCell: { text: 'Mesclar células' },
        }
      }
    },
    clipboard: {
      matchVisual: false,
    },
    keyboard: {
      bindings: {
        tab: {
          key: 9,
          handler: function () {
            return true;
          }
        },
        'remove format': {
          key: 'E',
          ctrlKey: true,
          handler: function (range, context) {
            this.quill.removeFormat(range.index, range.length);
          }
        }
      }
    },
    history: {
      delay: 2000,
      maxStack: 500,
      userOnly: true
    },
  }), [imageHandler]);

  const wordCount = useMemo(() => {
    return content.replace(/<[^>]+>/g, " ").trim().split(/\s+/).filter(Boolean).length;
  }, [content]);

  const charCount = useMemo(() => {
    return content.replace(/<[^>]+>/g, "").length;
  }, [content]);

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #ddd",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: "300px",
      }}
    >
      <QuillEditor
        ref={quillRef}
        theme="snow"
        value={content}
        onChange={handleChange}
        modules={modules}
        style={{
          minHeight: "250px",
          flex: 1,
          border: "none",
        }}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "10px",
          fontSize: "14px",
          color: "#555",
          borderTop: "1px solid #eee",
          background: "#f9f9f9",
        }}
      >
        <span>{wordCount} Palavras</span>
        <span>{charCount} Caracteres</span>
      </div>
    </div>
  );
}